import React, { useCallback, useMemo, useState } from 'react';
import { Layout } from 'tea-component';
import useLanguage from '@op/hooks/useLanguage';
import SvgIcon from '@home/components/SvgIcon';
import { connect, useHistory, useParams } from 'umi';
import ContentBody from '@op/components/ContentBody';
import { BrushSelectType, IRealtimeStreaming } from '../type';
import RealtimeTrendAnalysis from './component/RealtimeTrendAnalysis';
import CardCommon from '@op/components/CardCommon';
import StreamingDetail from './component/StreamingDetail';
import useStreamingDetail from '../hooks/useStreamingDetail';
import StreamingMetrics from './component/StreamingMetrics';
import LiveChartsAnalysis from './component/LiveChartsAnalysis';
import LiveStreamingContentAnalysis from './component/LiveStreamingContentAnalysis';
import _ from 'lodash-es';
import './index.scss';
import { useStateRef } from '@op/hooks/common';
import { RealtimeStreamingDetail as RealtimeStreamingDetailPoint } from '@op/operationLog/pages';
import { useInitBuriedPointInfo } from '@op/operationLog/buriedPoint';
import { DBO_TM, DBO_U } from '@op/utils/format';
import logo from '@op/assets/imgs/imarketing_logo.png';
import { getLiveTimeFilter } from '../utils';
import moment from 'moment';
import { realtimeStreamingDLTBTips } from '@op/components/BubbleCommon';
import { DLTB_GAME_ID, GST_GAME_ID } from '@op/constants/specialGame';

const { Content } = Layout;
const RealtimeStreamingDetails = (props: IRealtimeStreaming) => {
  const { gameInfo, wholeGameInfo } = props;
  const { formatMessage } = useLanguage();
  const { id } = useParams();
  const timezone = DBO_TM.timezone();
  const { isDLTB, isGST } = useMemo(() => {
    const gameId = gameInfo?.edition_unified_id;
    const isGST = gameId === GST_GAME_ID;
    const isDLTB = gameId === DLTB_GAME_ID;
    return { isDLTB, isGST };
  }, [gameInfo]);

  useInitBuriedPointInfo({ buriedPoint: RealtimeStreamingDetailPoint });

  const history = useHistory();
  const [timeFilterValue, setTimeFilterValue] = useState<{
    startTime: string;
    endTime: string;
  }>();
  const [filterTimeRef, filterTime, setFilterTime] = useStateRef<{
    start_time?: string;
    end_time?: string;
  }>();
  const [hideContentAnalysis, setHideContentAnalysis] = useState(false);

  const isEmpty = useMemo(() => {
    const data = DBO_U.queryObj(location.search);
    const { start_time, end_time } = data;
    return (
      !moment(decodeURIComponent(start_time)).isValid() ||
      !moment(decodeURIComponent(end_time)).isValid()
    );
  }, []);

  const {
    data,
    loading,
    hasLiveCharts,
    hasStreaming,
    timelineFilter,
    time,
    addRefreshTask,
  } = useStreamingDetail({
    id,
    gameInfo,
    setFilterTime,
    setTimeFilterValue,
    isEmpty,
    isGST
  });

  function rangeTimeChangeFilterValue(value: {
    startTime: string;
    endTime: string;
  }) {
    const { startTime, endTime } = value;
    const liveTime = data.live_time;

    const start_time = DBO_TM.add(liveTime, startTime);
    const end_time = DBO_TM.add(liveTime, endTime);

    setFilterTime({ start_time, end_time });
  }

  const [liveTime, setLiveTime] = useState<{
    startTime: string;
    endTime: string;
  }>();

  const filter = useMemo(() => {
    if (!filterTime) {
      return null;
    }

    return {
      live_id: id,
      ...filterTime,
      date_type: 'hourly',
      ...gameInfo,
    };
  }, [filterTime]);

  const handleBrushSelect = useCallback((data: BrushSelectType) => {
    if (!_.isEqual(data.filter, filterTimeRef.current)) {
      setFilterTime(data.filter);
      setLiveTime(data.liveTime);
    }
  }, []);

  return (
    <Content className="dbo-realtime-streaming-details-wrapper">
      <ContentBody
        contentHeard={
          <Content.Header
            showBackButton
            onBackButtonClick={() =>
              history.push(
                `/opinion/Realtime/RealtimeStreaming?gameid=${gameInfo.edition_unified_id}`,
                { useStorageFilter: true },
              )
            }
            className="dbo-header"
            title={formatMessage('Details Page')}
            subtitle={
              <div className="dbo-header-subtitle dbo-header-subtitle-time">
                <SvgIcon type="clock" />
                {formatMessage(
                  { id: 'Update Time: {time} (UTC+{timezone})' },
                  { time, timezone },
                )}
              </div>
            }
            operation={
              <div
                style={{
                  display: 'inline-flex',
                  color: '#838697',
                  fontSize: 14,
                  alignItems: 'center',
                }}
              >
                <img
                  src={logo}
                  style={{ width: 16, height: 16, marginRight: 6 }}
                />
                {formatMessage(
                  'Powered by iMarketing, contact bourneli@global.tencent.com for further questions',
                )}
              </div>
            }
          ></Content.Header>
        }
        horseRaceLampTips={isDLTB ? realtimeStreamingDLTBTips : undefined}
      >
        <CardCommon
          title={
            !isEmpty ? (
              <div className="dbo-streaming-detail-title">
                {formatMessage('Real-time Streaming Analysis')}
                <span
                  className="dbo-streaming-detail-live-url"
                  onClick={() => DBO_U.open(data.live_url)}
                >
                  {data?.live_url}
                </span>
              </div>
            ) : undefined
          }
          hoverNoShadow
          bodyStyle={{ backgroundColor: '#EBECF1' }}
          loading={loading}
          loadingHeight={700}
          empty={isEmpty}
          emptyHeight={700}
          emptyTitleText={
            'Unable to parse the link. Please ensure the link format is correct and reload the page.'
          }
          hasEmptyImg={false}
          hasEmptyDesc={false}
        >
          <>
            <div
              className="dbo-realtime-streaming-background"
              style={{ backgroundImage: `url(${wholeGameInfo.entity_image})` }}
            />
            <div className="dbo-realtime-streaming-mask"></div>
            <StreamingDetail
              hasStreaming={hasStreaming}
              data={data}
              addRefreshTask={addRefreshTask}
              setHideContentAnalysis={setHideContentAnalysis}
            ></StreamingDetail>
            <StreamingMetrics data={data}></StreamingMetrics>
            {hasLiveCharts && (
              <RealtimeTrendAnalysis
                filter={timelineFilter}
                onBrushSelect={handleBrushSelect}
                addRefreshTask={addRefreshTask}
                gameInfo={gameInfo}
                emptyCallback={() => handleBrushSelect(getLiveTimeFilter(data))}
                isGST={isGST}
              />
            )}
            <CardCommon contentCard loading={!filterTime} loadingHeight={278}>
              <>
                {hasStreaming && !hideContentAnalysis && (
                  <LiveStreamingContentAnalysis
                    filter={filter}
                    liveTime={liveTime}
                    setFilterTime={rangeTimeChangeFilterValue}
                    hasLiveCharts={hasLiveCharts}
                    timeFilterValue={timeFilterValue}
                    setTimeFilterValue={setTimeFilterValue}
                    realLiveTime={data.live_time}
                  ></LiveStreamingContentAnalysis>
                )}
                {/* GST 无 has_live_chats_analysis 数据，hasLiveCharts 为 false（不渲染趋势分析），但业务上仍需展示 LiveChartsAnalysis（内部走 content_urls 查询） */}
                {(hasLiveCharts || isGST) && (
                  <LiveChartsAnalysis
                    filter={filter}
                    liveTime={liveTime}
                    liveName={data.live_anchors!}
                    data={data}
                    isGST={isGST}
                  ></LiveChartsAnalysis>
                )}
              </>
            </CardCommon>
          </>
        </CardCommon>
      </ContentBody>
    </Content>
  );
};

export default connect((state: any) => {
  const { opinionCommon } = state;
  return {
    gameInfo: opinionCommon.gameInfo,
    wholeGameInfo: opinionCommon.wholeGameInfo,
  };
})(RealtimeStreamingDetails);
